import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Camera, Check, Loader2, Star, Trash2, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../api";
import {
  DRINKING_OPTIONS,
  EDUCATION_LEVELS,
  FOOD_PREFERENCES,
  INTERESTS,
  MARITAL_STATUSES,
  MAX_PHOTOS,
  RELIGIONS,
  SMOKING_OPTIONS,
  WANTS_CHILDREN_OPTIONS,
} from "../../../constants";
import { MIN_PHOTOS_HINT } from "../constants";
import type { Language, Profile } from "../../../types";
import { DateOfBirthField } from "../DateOfBirthField";
import { HeightPicker } from "../HeightPicker";
import { ProtectedImage } from "../ProtectedImage";
import { OnboardingFormLayout } from "../OnboardingProgress";
import { countryUsesState } from "../locationUtils";
import { useIsMobile } from "../useIsMobile";
import {
  prepareImageUploadFile,
  validateSourcePhoto,
} from "../../../image";
import {
  ErrorBanner,
  Field,
  GenderField,
  IdentityFieldsNote,
  ProfileFieldGroup,
  ProfileFirstNameField,
  ProfileLocationFields,
  ProfileLocationPrivacyNote,
  ProfileSection,
  SelectField,
  profilePhotoItems,
} from "./ProfileFormFields";
import { ProfileLanguagesField } from "./ProfileLanguagesField";
import { RELOCATION_OPTIONS, applyFormControlValidation, isValidatedFormControl, normalizeProfilePayload, profilePayloadEqual, scrollFormFieldIntoView, scrollProfileSectionIntoView, scrollValidationTargetIntoView } from "./profileFormUtils";

function FormButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: "primary" | "secondary" | "ghost" | "danger";
    loading?: boolean;
  },
) {
  const { className = "", variant = "primary", loading, children, disabled, ...rest } = props;
  return (
    <button className={`button ${variant} ${className}`} disabled={disabled || loading} {...rest}>
      {loading && <Loader2 className="spin" size={16} />}
      {children}
    </button>
  );
}

export function ProfileForm({
  initial,
  onSubmit,
  submitLabel,
  variant = "setup",
  formId = "profile-form",
  onboardingStep,
  onboardingBackTo,
  onDirtyChange,
  onSavingChange,
}: {
  initial?: Partial<Profile>;
  onSubmit: (profile: Partial<Profile>, photos: File[]) => Promise<void>;
  submitLabel: string;
  variant?: "setup" | "edit";
  formId?: string;
  onboardingStep?: "profile" | "preferences" | "match";
  onboardingBackTo?: string;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}) {
  const mobile = useIsMobile();
  const queryClient = useQueryClient();
  const [baseline, setBaseline] = useState<Partial<Profile> | null>(null);
  const [profile, setProfile] = useState<Partial<Profile>>(() => normalizeProfilePayload(initial));
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<
    { file: File; name: string; url: string }[]
  >([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fieldValidation, setFieldValidation] = useState<{ fieldKey: string; message: string } | null>(null);
  const [sectionError, setSectionError] = useState<{ sectionKey: string; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const showSectionCopy = variant !== "edit" || !mobile;

  const deletePhotoMutation = useMutation({
    mutationFn: api.deletePhoto,
    onSuccess: async (_data, photoId) => {
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] === "protected-photo" && query.queryKey[2] === photoId,
      });
      await queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: setError,
  });
  const profilePictureMutation = useMutation({
    mutationFn: api.setProfilePicture,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: setError,
  });

  const fieldError = (fieldKey: string) => (fieldValidation?.fieldKey === fieldKey ? fieldValidation.message : undefined);
  const sectionErrorMessage = (sectionKey: string) =>
    sectionError?.sectionKey === sectionKey ? sectionError.message : undefined;

  const clearFieldValidation = (fieldKey: string) => {
    setFieldValidation((current) => (current?.fieldKey === fieldKey ? null : current));
  };

  const clearSectionError = (sectionKey: string) => {
    setSectionError((current) => (current?.sectionKey === sectionKey ? null : current));
  };

  useEffect(() => {
    if (!initial) return;
    const next = normalizeProfilePayload(initial);
    setProfile(next);
    setBaseline(next);
  }, [initial]);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];
    setPhotoPreviews([]);

    void (async () => {
      const previews: { file: File; name: string; url: string }[] = [];
      for (const photo of photos) {
        try {
          const prepared = await prepareImageUploadFile(photo);
          if (cancelled) return;
          const url = URL.createObjectURL(prepared.thumbnail);
          objectUrls.push(url);
          previews.push({ file: photo, name: photo.name, url });
          setPhotoPreviews([...previews]);
        } catch (previewError) {
          if (cancelled) return;
          setError(previewError);
          setPhotos((current) => current.filter((candidate) => candidate !== photo));
          return;
        }
      }
    })();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [photos]);

  const isDirty = useMemo(
    () => (baseline ? !profilePayloadEqual(profile, baseline) || photos.length > 0 : variant === "setup"),
    [baseline, photos.length, profile, variant],
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onSavingChange?.(saving);
  }, [onSavingChange, saving]);

  useEffect(() => {
    if (!fieldValidation) return;
    const form = document.getElementById(formId);
    if (!form) return;
    const control = form.querySelector(`[data-profile-field="${fieldValidation.fieldKey}"]`);
    if (!(control instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      scrollValidationTargetIntoView(control);
    });
  }, [fieldValidation, formId]);

  useEffect(() => {
    if (!sectionError) return;
    const form = document.getElementById(formId);
    if (!form) return;

    requestAnimationFrame(() => {
      scrollProfileSectionIntoView(form, sectionError.sectionKey);
    });
  }, [sectionError, formId]);

  const existingPhotos = profilePhotoItems({ ...(profile as Profile), photos: initial?.photos || [] });
  const totalPhotoCount = existingPhotos.length + photos.length;
  const newPhotoCap = Math.max(0, MAX_PHOTOS - existingPhotos.length);
  const remainingPhotoSlots = Math.max(0, newPhotoCap - photos.length);
  const hasLocation = !!(profile.city && profile.country);

  const update = <K extends keyof Profile>(key: K, value: Profile[K]) => {
    clearFieldValidation(String(key));
    setProfile((current) => ({ ...current, [key]: value }));
  };
  const toggleInterest = (interest: string) => {
    const current = profile.interests || [];
    const next = current.includes(interest) ? current.filter((item) => item !== interest) : [...current, interest];
    if (next.length > 0) {
      clearSectionError("interests");
    }
    update("interests", next);
  };
  const updateLanguages = (languages: Language[]) => {
    clearFieldValidation("languages");
    clearSectionError("languages");
    update("languages", languages);
  };

  const locate = async () => {
    setLocationLoading(true);
    clearSectionError("location");
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 12000 }));
      const result = await api.reverseGeocode(position.coords.latitude, position.coords.longitude);
      setProfile((current) => ({
        ...current,
        city: result.city,
        country: result.country,
        state: countryUsesState(result.country) ? (result.state || "") : "",
      }));
      clearSectionError("location");
    } catch (err) {
      setSectionError({
        sectionKey: "location",
        message: err instanceof Error ? err.message : "Could not get your location",
      });
    } finally {
      setLocationLoading(false);
    }
  };

  const savedPhotoCount = existingPhotos.length;

  const handlePhotos = useCallback((files: File[]) => {
    if (!files.length) return;
    setError(null);
    const accepted: File[] = [];
    for (const file of files) {
      try {
        validateSourcePhoto(file);
        accepted.push(file);
      } catch (selectionError) {
        setError(selectionError);
      }
    }
    if (!accepted.length) return;

    setPhotos((current) => {
      const cap = Math.max(0, MAX_PHOTOS - savedPhotoCount);
      const remaining = cap - current.length;
      if (remaining <= 0) return current;
      return [...current, ...accepted.slice(0, remaining)];
    });
  }, [savedPhotoCount]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setError(null);
    setFieldValidation(null);
    setSectionError(null);

    if (!form.checkValidity()) {
      const firstInvalid = form.querySelector("input:invalid, textarea:invalid, select:invalid");
      if (isValidatedFormControl(firstInvalid)) {
        applyFormControlValidation(firstInvalid, setFieldValidation);
      }
      return;
    }

    if (!profile.dateOfBirth?.trim()) {
      setFieldValidation({ fieldKey: "dateOfBirth", message: "Date of Birth is required." });
      return;
    }

    const languages = profile.languages ?? [];
    const languageKeys = languages.map(({ language }) => (
      language.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("en-US")
    ));
    if (languages.length === 0 || languageKeys.some((language) => !language)) {
      setSectionError({ sectionKey: "languages", message: "Add at least one language." });
      return;
    }
    if (new Set(languageKeys).size !== languageKeys.length) {
      setSectionError({ sectionKey: "languages", message: "List each language only once." });
      return;
    }

    if (!profile.interests?.length) {
      setSectionError({ sectionKey: "interests", message: "Choose at least one interest." });
      return;
    }
    if (!profile.city?.trim() || !profile.country?.trim()) {
      setSectionError({ sectionKey: "location", message: "Add your location before continuing." });
      return;
    }

    setSaving(true);
    try {
      const payload = normalizeProfilePayload(profile);
      await onSubmit(payload, photos);
      if (variant === "edit") {
        setBaseline(payload);
        setPhotos([]);
      }
    } catch (err) {
      setError(err);
      requestAnimationFrame(() => {
        const banner = form.querySelector(".banner.error");
        if (banner instanceof HTMLElement) {
          scrollFormFieldIntoView(banner);
        }
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInvalid = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const field = event.target;
    if (!isValidatedFormControl(field)) return;
    setSectionError(null);
    applyFormControlValidation(field, setFieldValidation);
  };

  const mobileGroupedForm = mobile;

  const lifeFields = (
    <>
      <SelectField label="Marital Status" value={(profile.maritalHistory || "never_married") as any} onChange={(value) => update("maritalHistory", value as any)} options={MARITAL_STATUSES as any} />
      <div className="inline-fields">
        <SelectField label="Wants Children" value={(profile.wantsChildren || "not_sure") as any} onChange={(value) => update("wantsChildren", value as any)} options={WANTS_CHILDREN_OPTIONS as any} />
        <SelectField label="Education" value={(profile.educationLevel || "bachelors") as any} onChange={(value) => update("educationLevel", value as any)} options={EDUCATION_LEVELS as any} />
      </div>
      <div className="inline-fields">
        <SelectField label="Smoking" value={(profile.smoking || "never") as any} onChange={(value) => update("smoking", value as any)} options={SMOKING_OPTIONS as any} />
        <SelectField label="Drinking" value={(profile.drinking || "never") as any} onChange={(value) => update("drinking", value as any)} options={DRINKING_OPTIONS as any} />
      </div>
      <div className="inline-fields">
        <SelectField label="Food Preference" value={(profile.foodPreference || "all_foods") as any} onChange={(value) => update("foodPreference", value as any)} options={FOOD_PREFERENCES as any} />
        <Field label="Occupation"><input value={profile.occupation || ""} onChange={(event) => update("occupation", event.target.value)} /></Field>
      </div>
    </>
  );

  const relocationField = (
    <SelectField
      label="How far would you move?"
      value={(profile.relocationWillingness || "same_country") as any}
      onChange={(value) => update("relocationWillingness", value as any)}
      options={RELOCATION_OPTIONS as any}
    />
  );

  const photoFields = (
    <>
      <div className="photo-field">
        <label
          className={["photo-upload", remainingPhotoSlots <= 0 ? "is-disabled" : ""].filter(Boolean).join(" ")}
          aria-disabled={remainingPhotoSlots <= 0}
        >
          <input
            className="photo-upload-input"
            type="file"
            accept="image/*"
            multiple
            disabled={remainingPhotoSlots <= 0}
            onChange={(event) => {
              const selected = Array.from(event.target.files ?? []);
              event.currentTarget.value = "";
              handlePhotos(selected);
            }}
          />
          <span className="photo-upload-icon"><Camera size={22} /></span>
          <span className="photo-upload-copy">
            <strong>{remainingPhotoSlots <= 0 ? "Photo limit reached" : photos.length ? "Add more photos" : "Add photos"}</strong>
            <small>
              {existingPhotos.length
                ? `${existingPhotos.length} saved photo${existingPhotos.length === 1 ? "" : "s"}`
                : remainingPhotoSlots === newPhotoCap
                  ? `Choose up to ${newPhotoCap} photos at once`
                  : `Add up to ${remainingPhotoSlots} more photo${remainingPhotoSlots === 1 ? "" : "s"}`}
            </small>
          </span>
          <span className="photo-upload-count">{totalPhotoCount}/{MAX_PHOTOS}</span>
        </label>
      </div>
      {!!existingPhotos.length && (
        <div className="profile-form-photo-group">
          <span>Saved photos</span>
          {existingPhotos.some((photo) => !photo.isProfilePicture) && (
            <p className="profile-form-photo-hint">
              Select the star on a photo to make it your profile picture.
            </p>
          )}
          <div className="photo-preview-row">
            {existingPhotos.map((photo, index) => (
              <div className="photo-preview photo-preview--saved" key={photo.id}>
                <ProtectedImage photoId={photo.id} alt={`Saved profile photo ${index + 1}`} />
                {photo.isProfilePicture && <span className="photo-preview-badge">Profile</span>}
                <div className="photo-preview-actions">
                  {!photo.isProfilePicture && (
                    <button
                      type="button"
                      disabled={profilePictureMutation.isPending || deletePhotoMutation.isPending}
                      onClick={() => profilePictureMutation.mutate(photo.id)}
                      aria-label={`Make saved profile photo ${index + 1} the profile picture`}
                      title="Make this your profile picture"
                    >
                      {profilePictureMutation.isPending && profilePictureMutation.variables === photo.id
                        ? <Loader2 className="spin" size={14} />
                        : <Star size={14} />}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={deletePhotoMutation.isPending || profilePictureMutation.isPending}
                    onClick={() => {
                      if (window.confirm("Delete this photo permanently?")) {
                        deletePhotoMutation.mutate(photo.id);
                      }
                    }}
                    aria-label={`Delete saved profile photo ${index + 1}`}
                  >
                    {deletePhotoMutation.isPending && deletePhotoMutation.variables === photo.id
                      ? <Loader2 className="spin" size={14} />
                      : <Trash2 size={14} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {!!photos.length && (
        <div className="profile-form-photo-group">
          <span>
            {photoPreviews.length < photos.length
              ? <>Preparing photos ({photoPreviews.length}/{photos.length}) <Loader2 className="spin" size={14} /></>
              : "New photos"}
          </span>
          <div className="photo-preview-row">
            {photoPreviews.map((photo, index) => (
              <div className="photo-preview" key={`${photo.name}-${index}`}>
                <img src={photo.url} alt="" decoding="async" />
                <button type="button" onClick={() => setPhotos((current) => current.filter((candidate) => candidate !== photo.file))} aria-label={`Remove ${photo.name}`}>
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const formSections = mobile ? (
    <>
      <section className="preferences-section" data-profile-section="basics">
        <div className="preference-group-head">
          <span>Basics</span>
          {showSectionCopy ? <p>Basic profile details shown with your introduction.</p> : null}
        </div>
        <ProfileFieldGroup footnote={<IdentityFieldsNote />}>
          <ProfileFirstNameField
            label="First name"
            value={profile.firstName || ""}
            onChange={(value) => update("firstName", value)}
            locked={variant === "edit"}
            error={fieldError("firstName")}
          />
          <DateOfBirthField
            value={profile.dateOfBirth || ""}
            onChange={(value) => update("dateOfBirth", value)}
            required
            locked={variant === "edit"}
            error={fieldError("dateOfBirth")}
          />
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Gender">
          <GenderField
            value={(profile.gender || "female") as "male" | "female"}
            onChange={(value) => update("gender", value)}
            locked={variant === "edit"}
          />
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Your height">
          <HeightPicker showToolbarLabel={false} value={profile.height || 170} onChange={(value) => update("height", value)} label="Height" />
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Faith">
          <SelectField label="Religion" value={(profile.religion || "bahai") as any} onChange={(value) => update("religion", value as any)} options={RELIGIONS as any} />
        </ProfileFieldGroup>
      </section>

      <section className="preferences-section" data-profile-section="location">
        <div className="preference-group-head">
          <span>Place & reach</span>
          {showSectionCopy ? <p>Only your city, state, and country are shared — not your exact location.</p> : null}
        </div>
        <ProfileFieldGroup invalid={!!sectionErrorMessage("location")}>
          <ProfileLocationFields
            profile={profile}
            hasLocation={hasLocation}
            locationLoading={locationLoading}
            onLocate={locate}
            error={sectionErrorMessage("location")}
          />
        </ProfileFieldGroup>
        {!showSectionCopy ? <ProfileLocationPrivacyNote /> : null}
        <ProfileFieldGroup subhead="How far would you move?">
          {relocationField}
        </ProfileFieldGroup>
      </section>

      <section className="preferences-section" data-profile-section="life">
        <div className="preference-group-head">
          <span>Life & lifestyle</span>
          {showSectionCopy ? <p>Context that helps someone understand your stage of life.</p> : null}
        </div>
        <ProfileFieldGroup subhead="Marital status">
          <SelectField label="Marital status" value={(profile.maritalHistory || "never_married") as any} onChange={(value) => update("maritalHistory", value as any)} options={MARITAL_STATUSES as any} />
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Wants children">
          <SelectField label="Wants children" value={(profile.wantsChildren || "not_sure") as any} onChange={(value) => update("wantsChildren", value as any)} options={WANTS_CHILDREN_OPTIONS as any} />
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Education">
          <SelectField label="Education" value={(profile.educationLevel || "bachelors") as any} onChange={(value) => update("educationLevel", value as any)} options={EDUCATION_LEVELS as any} />
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Smoking">
          <SelectField label="Smoking" value={(profile.smoking || "never") as any} onChange={(value) => update("smoking", value as any)} options={SMOKING_OPTIONS as any} />
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Drinking">
          <SelectField label="Drinking" value={(profile.drinking || "never") as any} onChange={(value) => update("drinking", value as any)} options={DRINKING_OPTIONS as any} />
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Food preference">
          <SelectField label="Food preference" value={(profile.foodPreference || "all_foods") as any} onChange={(value) => update("foodPreference", value as any)} options={FOOD_PREFERENCES as any} />
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Occupation">
          <Field label="Occupation"><input value={profile.occupation || ""} onChange={(event) => update("occupation", event.target.value)} /></Field>
        </ProfileFieldGroup>
      </section>

      <section className="preferences-section" data-profile-section="languages">
        <div className="preference-group-head">
          <span>Languages</span>
          {showSectionCopy ? <p>Add the languages you can comfortably use to communicate.</p> : null}
        </div>
        <ProfileFieldGroup invalid={!!(sectionErrorMessage("languages") ?? fieldError("languages"))}>
          <ProfileLanguagesField
            languages={profile.languages ?? []}
            onChange={updateLanguages}
            error={sectionErrorMessage("languages") ?? fieldError("languages")}
          />
        </ProfileFieldGroup>
      </section>

      <section className="preferences-section" data-profile-section="about">
        <div className="preference-group-head">
          <span>About you</span>
          {showSectionCopy ? <p>These become the main cards people read on your profile.</p> : null}
        </div>
        <ProfileFieldGroup subhead="About me">
          <Field label="About me" error={fieldError("aboutMe")}>
            <textarea
              value={profile.aboutMe || ""}
              minLength={50}
              data-profile-field="aboutMe"
              onChange={(event) => update("aboutMe", event.target.value)}
              required
            />
          </Field>
        </ProfileFieldGroup>
        <ProfileFieldGroup subhead="Looking for">
          <Field label="Looking for" error={fieldError("lookingFor")}>
            <textarea
              value={profile.lookingFor || ""}
              minLength={50}
              data-profile-field="lookingFor"
              onChange={(event) => update("lookingFor", event.target.value)}
              required
            />
          </Field>
        </ProfileFieldGroup>
      </section>

      <section className="preferences-section" data-profile-section="interests">
        <div className="preference-group-head">
          <span>Interests</span>
          {showSectionCopy ? <p>Displayed for conversation, not used as matching filters.</p> : null}
        </div>
        <ProfileFieldGroup invalid={!!sectionErrorMessage("interests")}>
          <div className="chip-grid profile-form-chip-grid">
            {INTERESTS.map((interest) => <button className={`chip ${(profile.interests || []).includes(interest) ? "selected" : ""}`} type="button" key={interest} onClick={() => toggleInterest(interest)}>{interest}</button>)}
          </div>
          <p className={sectionErrorMessage("interests") ? "field-error" : "field-note"} role={sectionErrorMessage("interests") ? "alert" : undefined}>
            {sectionErrorMessage("interests") ?? "Choose at least one interest."}
          </p>
        </ProfileFieldGroup>
      </section>

      <section className="preferences-section" data-profile-section="photos">
        <div className="preference-group-head">
          <span>Photos</span>
          {showSectionCopy ? <p>{`Upload clear, recent photos. At least ${MIN_PHOTOS_HINT} photos are recommended for matching.`}</p> : null}
        </div>
        <ProfileFieldGroup>{photoFields}</ProfileFieldGroup>
      </section>
    </>
  ) : (
    <>
      <ProfileSection
        mobile={mobile}
        sectionKey="basics"
        title="Identity"
        description={showSectionCopy ? "Basic profile details shown with your introduction." : undefined}
      >
        <ProfileFirstNameField
          label="First Name"
          value={profile.firstName || ""}
          onChange={(value) => update("firstName", value)}
          locked={variant === "edit"}
          error={fieldError("firstName")}
        />
        <DateOfBirthField
          label="Date of Birth"
          value={profile.dateOfBirth || ""}
          onChange={(value) => update("dateOfBirth", value)}
          required
          locked={variant === "edit"}
          error={fieldError("dateOfBirth")}
        />
        <IdentityFieldsNote />
        <GenderField
          value={(profile.gender || "female") as "male" | "female"}
          onChange={(value) => update("gender", value)}
          locked={variant === "edit"}
        />
        <div className="inline-fields">
          <HeightPicker value={profile.height || 170} onChange={(value) => update("height", value)} label="Height" />
          <SelectField label="Religion" value={(profile.religion || "bahai") as any} onChange={(value) => update("religion", value as any)} options={RELIGIONS as any} />
        </div>
      </ProfileSection>

      <ProfileSection
        mobile={mobile}
        sectionKey="location"
        title="Location"
        description={showSectionCopy ? "Only your city, state, and country are shared — not your exact location." : undefined}
        invalid={!!sectionErrorMessage("location")}
      >
        <ProfileLocationFields
          profile={profile}
          hasLocation={hasLocation}
          locationLoading={locationLoading}
          onLocate={locate}
          integrated
          error={sectionErrorMessage("location")}
        />
        {!showSectionCopy ? <ProfileLocationPrivacyNote /> : null}
        {relocationField}
      </ProfileSection>

      <ProfileSection
        mobile={mobile}
        sectionKey="life"
        title="Life details"
        description={showSectionCopy ? "Context that helps someone understand your stage of life." : undefined}
      >
        {lifeFields}
      </ProfileSection>

      <ProfileSection
        mobile={mobile}
        sectionKey="languages"
        title="Languages"
        description={showSectionCopy ? "Add the languages you can comfortably use to communicate." : undefined}
        invalid={!!(sectionErrorMessage("languages") ?? fieldError("languages"))}
      >
        <ProfileLanguagesField
          languages={profile.languages ?? []}
          onChange={updateLanguages}
          error={sectionErrorMessage("languages") ?? fieldError("languages")}
        />
      </ProfileSection>

      <ProfileSection
        mobile={mobile}
        sectionKey="about"
        title="About"
        description={showSectionCopy ? "These become the main cards people read on your profile." : undefined}
        className="profile-form-section--story"
      >
        <Field label="About Me" error={fieldError("aboutMe")}>
          <textarea
            value={profile.aboutMe || ""}
            minLength={50}
            data-profile-field="aboutMe"
            onChange={(event) => update("aboutMe", event.target.value)}
            required
          />
        </Field>
        <Field label="Looking For" error={fieldError("lookingFor")}>
          <textarea
            value={profile.lookingFor || ""}
            minLength={50}
            data-profile-field="lookingFor"
            onChange={(event) => update("lookingFor", event.target.value)}
            required
          />
        </Field>
      </ProfileSection>

      <ProfileSection
        mobile={mobile}
        sectionKey="interests"
        title="Interests"
        description={showSectionCopy ? "Displayed for conversation, not used as matching filters." : undefined}
        invalid={!!sectionErrorMessage("interests")}
      >
        <div className="chip-grid profile-form-chip-grid">
          {INTERESTS.map((interest) => <button className={`chip ${(profile.interests || []).includes(interest) ? "selected" : ""}`} type="button" key={interest} onClick={() => toggleInterest(interest)}>{interest}</button>)}
        </div>
        <p className={sectionErrorMessage("interests") ? "field-error" : "field-note"} role={sectionErrorMessage("interests") ? "alert" : undefined}>
          {sectionErrorMessage("interests") ?? "Choose at least one interest."}
        </p>
      </ProfileSection>

      <ProfileSection mobile={mobile} sectionKey="photos" title="Photos" description={showSectionCopy ? `Upload clear, recent photos. At least ${MIN_PHOTOS_HINT} photos are recommended for matching.` : undefined}>
        {photoFields}
      </ProfileSection>
    </>
  );

  const formBody = (
    <>
      <ErrorBanner error={error} />
      {formSections}
    </>
  );

  return (
    <form
      className={[
        "form",
        "profile-form",
        `profile-form--${variant}`,
        mobileGroupedForm ? "preferences-form" : "",
        !mobile && variant === "setup" ? "card-form" : "",
      ].filter(Boolean).join(" ")}
      id={formId}
      onInvalidCapture={handleInvalid}
      onSubmit={submit}
    >
      <div className={mobileGroupedForm ? "preferences-form-scroll" : "profile-form-scroll"}>
        {onboardingStep ? (
          <OnboardingFormLayout step={onboardingStep} backTo={onboardingBackTo}>
            {formBody}
          </OnboardingFormLayout>
        ) : (
          formBody
        )}
      </div>
      {variant === "setup" && (
        <div className="profile-form-footer">
          <FormButton className="form-submit-button" loading={saving} type="submit"><Check size={16} /> {submitLabel}</FormButton>
        </div>
      )}
    </form>
  );
}
